import { API_BASE_URL } from "src/config/apiBase";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../CSS/Global.css";
import "./AdmissionImport.css";
import { readFirstSheetAsJson } from "../../utils/excelSheet";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileSpreadsheet, Upload } from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import normalizePhoneNumber from "../utils/normalizePhoneNumber";

/** Matches export / sample: `Admission.jsx` ADMISSION_SAMPLE_ROW and `exportadmission.jsx` cleanedData keys. */
const ADMISSION_FIELD_DEFS = [
    { key: "name", label: "Name", required: true },
    { key: "fatherName", label: "Father Name", required: false },
    { key: "motherName", label: "Mother Name", required: false },
    { key: "dob", label: "Date of Birth", required: false },
    { key: "aadhar", label: "Aadhar", required: false },
    { key: "gender", label: "Gender", required: false },
    { key: "school", label: "School", required: false },
    { key: "address", label: "Address", required: false },
    { key: "pincode", label: "Pincode", required: false },
    { key: "mobile", label: "Mobile", required: false },
    { key: "email", label: "Email", required: false },
    { key: "center", label: "Center", required: true },
    { key: "batchId", label: "Batch ID", required: false },
    { key: "batchName", label: "Batch Name", required: false },
    { key: "status", label: "Status", required: false },
    { key: "rulesAgreed", label: "Rules Agreed", required: false },
];

const FIELDS = ADMISSION_FIELD_DEFS.map((d) => d.key);

const FIELD_LABELS = Object.fromEntries(ADMISSION_FIELD_DEFS.map((d) => [d.key, d.label]));

const REQUIRED_KEYS = ADMISSION_FIELD_DEFS.filter((d) => d.required).map((d) => d.key);

/** Headers present on export; ignored when building create payload (IDs are generated server-side). */
const READ_ONLY_HEADERS = new Set(["Admission ID", "Payment ID", "Created At"]);

function normHeader(s) {
    return String(s ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

function parseAdmissionDate(val) {
    if (val == null || val === "") return "";
    if (val instanceof Date) {
        if (Number.isNaN(val.getTime())) return "";
        return val.toISOString().split("T")[0];
    }
    if (typeof val === "number") {
        if (val > 20000 && val < 120000) {
            const baseDate = new Date(1899, 11, 30);
            const corrected = new Date(baseDate.getTime() + (val + 1) * 86400000);
            if (!Number.isNaN(corrected.getTime())) return corrected.toISOString().split("T")[0];
        }
    }
    const s = String(val).trim();
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
    if (m) {
        let y = parseInt(m[3], 10);
        if (y < 100) y += y < 50 ? 2000 : 1900;
        return `${String(y).padStart(4, "0")}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split("T")[0];
    return s;
}

function normalizeRulesAgreed(val) {
    if (val == null || val === "") return "1";
    const s = String(val).trim().toLowerCase();
    if (["0", "no", "false", "n"].includes(s)) return "0";
    return "1";
}

function trimStr(v) {
    if (v == null || v === "") return "";
    return String(v).trim();
}

const PLACEHOLDER_MOBILE = new Set([
    "na",
    "n/a",
    "nil",
    "none",
    "-",
    "--",
    "0",
    "0000000000",
    "9999999999",
]);

/** Ignore placeholder / invalid mobiles so import rows do not share one customer_id via dedupe. */
function sanitizeImportMobile(mobileRaw) {
    let m = trimStr(mobileRaw);
    if (!m) return "";
    const lower = m.toLowerCase();
    if (PLACEHOLDER_MOBILE.has(lower)) return "";
    try {
        m = normalizePhoneNumber(String(m).replace(/\s/g, ""));
    } catch {
        m = String(mobileRaw).replace(/\D/g, "");
    }
    if (!/^[6-9]\d{9}$/.test(m)) return "";
    return m;
}

function sanitizeImportEmail(emailRaw) {
    const e = trimStr(emailRaw);
    if (!e) return "";
    const lower = e.toLowerCase();
    if (PLACEHOLDER_MOBILE.has(lower) || lower === "noemail" || lower === "no email") return "";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return "";
    return e;
}

/** Normalize mobile the same way as import payload (matches list comparison). */
function fingerprintMobile(mobileRaw) {
    let m = trimStr(mobileRaw);
    if (!m) return "";
    try {
        m = normalizePhoneNumber(String(m).replace(/\s/g, ""));
    } catch {
        m = String(mobileRaw).replace(/\D/g, "");
    }
    return m;
}

/**
 * Stable key for "same admission" — same Excel imported twice should hit this.
 * Uses name, parents, center, phone, email, DOB, aadhar digits, batch id.
 */
function admissionImportFingerprint(payload) {
    const bid =
        payload.batch_id != null && payload.batch_id !== ""
            ? String(parseInt(String(payload.batch_id), 10))
            : "";
    let dob = trimStr(payload.dob || "").slice(0, 10);
    const aadhar = String(trimStr(payload.aadhar || "")).replace(/\D/g, "");
    const parts = [
        trimStr(payload.name).toLowerCase(),
        trimStr(payload.fatherName).toLowerCase(),
        trimStr(payload.motherName).toLowerCase(),
        trimStr(payload.center).toLowerCase(),
        fingerprintMobile(payload.mobile),
        trimStr(payload.email).toLowerCase(),
        dob,
        aadhar,
        bid,
    ];
    return parts.join("\x1f");
}

function fingerprintFromExistingAdmission(a) {
    let dob = "";
    if (a?.dob) {
        const d = new Date(a.dob);
        if (!Number.isNaN(d.getTime())) dob = d.toISOString().slice(0, 10);
    }
    return admissionImportFingerprint({
        name: a?.name,
        fatherName: a?.fatherName,
        motherName: a?.motherName,
        center: a?.center,
        mobile: a?.mobile,
        email: a?.email,
        dob,
        aadhar: a?.aadhar,
        batch_id: a?.batch_id,
    });
}

/**
 * Rows that match an existing admission (same tenant) or repeat within this file.
 * @returns {{ rowIndex: number, reason: 'existing' | 'file' }[]}
 */
function findDuplicateImportIssues(validRows, existingFingerprintSet) {
    const seenInFile = new Set();
    const issues = [];
    for (const { rowIndex, payload } of validRows) {
        const fp = admissionImportFingerprint(payload);
        if (existingFingerprintSet.has(fp)) {
            issues.push({ rowIndex, reason: "existing" });
        } else if (seenInFile.has(fp)) {
            issues.push({ rowIndex, reason: "file" });
        }
        seenInFile.add(fp);
    }
    return issues;
}

function isBatchActive(b) {
    return String(b?.status || "").trim().toLowerCase() === "active";
}

/** Whether this center has at least one active batch (same rule as manual admission form). */
function centerHasActiveBatches(allBatches, rowCenter) {
    const c = String(rowCenter || "").trim();
    if (!c) return false;
    return allBatches.some(
        (b) => isBatchActive(b) && String(b.center_id || "").trim() === c
    );
}

/**
 * Resolve DB batch id for a row: explicit Batch ID, then Batch name (same center), then default batch if it matches row center.
 */
function resolveBatchForRow(allBatches, rowCenter, batchIdRaw, batchNameRaw, defaultBatchId) {
    const centerNorm = String(rowCenter || "").trim();
    const activeForCenter = allBatches.filter(
        (b) => isBatchActive(b) && String(b.center_id || "").trim() === centerNorm
    );

    const trimmedId = String(batchIdRaw ?? "").trim();
    if (trimmedId !== "") {
        const n = parseInt(trimmedId, 10);
        if (!Number.isNaN(n) && n > 0) {
            const b = allBatches.find((x) => Number(x.id) === n);
            if (
                b &&
                isBatchActive(b) &&
                String(b.center_id || "").trim() === centerNorm
            ) {
                return n;
            }
        }
    }

    const bn = String(batchNameRaw ?? "").trim().toLowerCase();
    if (bn) {
        const hit = activeForCenter.find((x) => String(x.name || "").trim().toLowerCase() === bn);
        if (hit) return Number(hit.id);
    }

    const def = String(defaultBatchId ?? "").trim();
    if (def !== "") {
        const d = parseInt(def, 10);
        if (!Number.isNaN(d) && d > 0) {
            const b = allBatches.find((x) => Number(x.id) === d);
            if (
                b &&
                isBatchActive(b) &&
                String(b.center_id || "").trim() === centerNorm
            ) {
                return d;
            }
        }
    }

    return null;
}

function rowToAdmissionPayload(row, fieldMappings, isLoggedIn, actingUserName) {
    const get = (key) => {
        const header = fieldMappings[key];
        if (!header || header === "none") return "";
        const raw = row[header];
        return raw;
    };

    const mobile = sanitizeImportMobile(get("mobile"));
    const email = sanitizeImportEmail(get("email"));

    return {
        name: trimStr(get("name")),
        fatherName: trimStr(get("fatherName")),
        motherName: trimStr(get("motherName")),
        dob: parseAdmissionDate(get("dob")),
        aadhar: trimStr(get("aadhar")),
        gender: trimStr(get("gender")),
        school: trimStr(get("school")),
        address: trimStr(get("address")),
        pincode: trimStr(get("pincode")),
        mobile,
        email,
        center: trimStr(get("center")),
        batchId: trimStr(get("batchId")),
        batchName: trimStr(get("batchName")),
        status: trimStr(get("status")),
        rulesAgreed: normalizeRulesAgreed(get("rulesAgreed")),
        main_id: isLoggedIn?.main_id || "",
        user_name: actingUserName,
    };
}

const AdmissionImport = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [fileName, setFileName] = useState("");
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [fieldMappings, setFieldMappings] = useState({});
    const [excelHeaders, setExcelHeaders] = useState([]);
    const [rawExcelData, setRawExcelData] = useState([]);
    const [validRows, setValidRows] = useState([]);
    const [skippedRecords, setSkippedRecords] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importStats, setImportStats] = useState({
        count: 0,
        skipped: 0,
        unmapped: 0,
    });
    const [batches, setBatches] = useState([]);
    const [batchesLoad, setBatchesLoad] = useState(true);
    const [defaultBatchId, setDefaultBatchId] = useState("");

    const url = API_BASE_URL;
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || "";
    const actingUserName = isLoggedIn?.full_name || isLoggedIn?.email || "Admin";

    const authHeaders = useMemo(
        () => ({
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }),
        [token]
    );

    useEffect(() => {
        if (!token) {
            setBatches([]);
            setBatchesLoad(false);
            return undefined;
        }
        let cancelled = false;
        setBatchesLoad(true);
        fetch(`${url}/admission/batches`, { headers: authHeaders })
            .then((r) => r.json())
            .then((j) => {
                if (!cancelled) setBatches(Array.isArray(j?.batches) ? j.batches : []);
            })
            .catch(() => {
                if (!cancelled) setBatches([]);
            })
            .finally(() => {
                if (!cancelled) setBatchesLoad(false);
            });
        return () => {
            cancelled = true;
        };
    }, [url, token, authHeaders]);

    const autoMapHeaders = (headers) => {
        const auto = {};
        const normHeaders = headers.map((h) => ({ raw: h, n: normHeader(h) }));
        ADMISSION_FIELD_DEFS.forEach(({ key, label }) => {
            const want = normHeader(label);
            const hit = normHeaders.find((h) => h.n === want);
            if (hit) auto[key] = hit.raw;
        });
        return auto;
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setUploadSuccess(false);
            return;
        }

        const allowedExtensions = ["csv", "xlsx", "xls"];
        const fileExtension = file.name.split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            toast.error("Invalid file format. Please upload CSV, XLSX, or XLS.", {
                position: "top-center",
                autoClose: 3000,
            });
            e.target.value = "";
            return;
        }

        setFileName(file.name);
        setUploadedFileName(file.name);

        const reader = new FileReader();
        reader.onload = async (evt) => {
            let data;
            try {
                data = await readFirstSheetAsJson(evt.target.result);
            } catch (err) {
                console.error(err);
                toast.error("Could not read spreadsheet file.", { position: "top-center", autoClose: 3000 });
                return;
            }

            if (!data.length) {
                toast.error("The file has no data rows.", { position: "top-center", autoClose: 3000 });
                return;
            }

            const headers = Object.keys(data[0] || {});
            setExcelHeaders(headers);
            setRawExcelData(data);
            setFieldMappings(autoMapHeaders(headers));
            setUploadSuccess(true);
        };
        reader.readAsBinaryString(file);
    };

    const recomputeStep2Stats = useCallback(() => {
        if (batchesLoad && token) {
            return;
        }

        const reasons = [];
        const valids = [];

        rawExcelData.forEach((row, index) => {
            const payload = rowToAdmissionPayload(row, fieldMappings, isLoggedIn, actingUserName);
            const rowNum = index + 2;
            const missing = REQUIRED_KEYS.filter((k) => !payload[k]);
            if (missing.length) {
                reasons.push(`Row ${rowNum}: missing ${missing.map((k) => FIELD_LABELS[k]).join(", ")}`);
                return;
            }

            const needsBatch = centerHasActiveBatches(batches, payload.center);
            const resolvedBatchId = resolveBatchForRow(
                batches,
                payload.center,
                payload.batchId,
                payload.batchName,
                defaultBatchId
            );
            if (needsBatch && resolvedBatchId == null) {
                reasons.push(
                    `Row ${rowNum}: center "${payload.center}" has active batches — add Batch ID or Batch Name (same center as the row), or pick a default batch below for that center.`
                );
                return;
            }

            const { batchId: _b1, batchName: _b2, ...rest } = payload;
            valids.push({
                rowIndex: rowNum,
                payload: { ...rest, batch_id: resolvedBatchId },
            });
        });

        const mappedCount = FIELDS.filter((k) => fieldMappings[k] && fieldMappings[k] !== "none").length;
        setImportStats({
            count: valids.length,
            skipped: rawExcelData.length - valids.length,
            unmapped: FIELDS.length - mappedCount,
        });
        setValidRows(valids);
        setSkippedRecords(reasons);
    }, [
        batchesLoad,
        token,
        rawExcelData,
        fieldMappings,
        isLoggedIn,
        actingUserName,
        batches,
        defaultBatchId,
    ]);

    useEffect(() => {
        if (
            currentStep >= 3 &&
            uploadSuccess &&
            rawExcelData.length > 0 &&
            !(batchesLoad && token)
        ) {
            recomputeStep2Stats();
        }
    }, [
        currentStep,
        defaultBatchId,
        batches,
        batchesLoad,
        token,
        uploadSuccess,
        rawExcelData.length,
        recomputeStep2Stats,
    ]);

    const handleNext = () => {
        if (currentStep === 1) {
            if (!uploadSuccess || rawExcelData.length === 0) {
                toast.warn("Upload a file first.", { position: "top-center" });
                return;
            }
        }
        if (currentStep === 2) {
            if (batchesLoad && token) {
                toast.warn("Still loading batches — wait a moment, then click Next again.", {
                    position: "top-center",
                });
                return;
            }
            recomputeStep2Stats();
        }
        if (currentStep < 3) setCurrentStep(currentStep + 1);
    };

    const handleBack = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const handleImport = async () => {
        if (!token) {
            toast.error("Not logged in. Please sign in again.", { position: "top-center" });
            return;
        }
        if (!isLoggedIn?.main_id) {
            toast.error("Missing account id. Please sign in again.", { position: "top-center" });
            return;
        }
        if (validRows.length === 0) {
            toast.warn("No valid rows to import.", { position: "top-center" });
            return;
        }
        if (importing) {
            return;
        }

        setImporting(true);
        let ok = 0;
        const failures = [];

        try {
            let existingFingerprintSet = new Set();
            try {
                const listRes = await fetch(`${url}/admission/alladmin/${isLoggedIn.main_id}`, {
                    headers: authHeaders,
                });
                const listData = await listRes.json().catch(() => ({}));
                const existingRows = Array.isArray(listData?.user) ? listData.user : [];
                for (const a of existingRows) {
                    try {
                        existingFingerprintSet.add(fingerprintFromExistingAdmission(a));
                    } catch {
                        /* skip malformed row */
                    }
                }
            } catch {
                toast.error("Could not load existing admissions to check for duplicates. Try again.", {
                    position: "top-center",
                });
                return;
            }

            const duplicateIssues = findDuplicateImportIssues(validRows, existingFingerprintSet);
            const duplicateRowIndexSet = new Set(duplicateIssues.map((x) => x.rowIndex));

            let rowsToImport = validRows;

            if (duplicateIssues.length > 0) {
                const nExisting = duplicateIssues.filter((x) => x.reason === "existing").length;
                const nFile = duplicateIssues.filter((x) => x.reason === "file").length;
                const summaryBits = [];
                if (nExisting) summaryBits.push(`${nExisting} match admissions already saved`);
                if (nFile) summaryBits.push(`${nFile} repeat another row in this file`);
                const summary = summaryBits.join("; ");

                const listHtml = duplicateIssues
                    .slice(0, 15)
                    .map((i) => {
                        const hint =
                            i.reason === "existing"
                                ? "matches an admission already in your list"
                                : "same data as an earlier row in this spreadsheet";
                        return `<li>Data row ${i.rowIndex}: ${hint}</li>`;
                    })
                    .join("");
                const more =
                    duplicateIssues.length > 15
                        ? `<p style="margin-top:8px;font-size:13px">…and ${duplicateIssues.length - 15} more.</p>`
                        : "";

                const result = await Swal.fire({
                    icon: "warning",
                    title: "Possible duplicate data",
                    html:
                        `<p style="text-align:left;margin:0 0 10px">We found <strong>${duplicateIssues.length}</strong> row(s) that look like duplicates` +
                        (summary ? ` (${summary}).` : ".") +
                        ` Please cross-check — you may be uploading the same spreadsheet again.</p>` +
                        `<ul style="text-align:left;margin:0 0 0 18px;padding:0;max-height:240px;overflow:auto;font-size:14px;line-height:1.45">${listHtml}</ul>` +
                        more +
                        `<p style="text-align:left;margin-top:14px;font-size:14px;color:#475569">Import only <strong>new</strong> rows skips the rows listed above.</p>`,
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: "Import only new rows",
                    denyButtonText: "Import all anyway",
                    cancelButtonText: "Cancel",
                    reverseButtons: true,
                });

                if (!result.isConfirmed && !result.isDenied) {
                    return;
                }
                if (result.isConfirmed) {
                    rowsToImport = validRows.filter((r) => !duplicateRowIndexSet.has(r.rowIndex));
                    if (rowsToImport.length === 0) {
                        toast.info("No rows left to import after skipping duplicates.", {
                            position: "top-center",
                        });
                        return;
                    }
                }
            }

            for (const { rowIndex, payload } of rowsToImport) {
                const body = {
                    name: payload.name,
                    fatherName: payload.fatherName,
                    motherName: payload.motherName,
                    aadhar: payload.aadhar,
                    gender: payload.gender,
                    school: payload.school,
                    address: payload.address,
                    pincode: payload.pincode,
                    mobile: payload.mobile,
                    email: payload.email,
                    center: payload.center,
                    rulesAgreed: payload.rulesAgreed,
                    user_name: actingUserName,
                    status: payload.status || "Active",
                };
                if (payload.dob) body.dob = payload.dob;
                if (payload.batch_id != null && payload.batch_id !== "") {
                    const bid = parseInt(String(payload.batch_id), 10);
                    if (!Number.isNaN(bid) && bid > 0) body.batch_id = bid;
                }

                try {
                    const res = await fetch(`${url}/admission/admin`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(body),
                    });
                    const data = await res.json().catch(() => ({}));
                    if (res.ok && !data.isError) {
                        ok += 1;
                    } else {
                        failures.push({
                            row: rowIndex,
                            error: data.error || data.message || `HTTP ${res.status}`,
                        });
                    }
                } catch (err) {
                    failures.push({ row: rowIndex, error: err.message || "Network error" });
                }
            }

            if (ok > 0) {
                toast.success(`Imported ${ok} admission(s).`, { position: "top-center", autoClose: 2500 });
            }
            if (failures.length) {
                const preview = failures
                    .slice(0, 5)
                    .map((f) => `Row ${f.row}: ${f.error}`)
                    .join("\n");
                toast.error(
                    `${failures.length} row(s) failed.${failures.length > 5 ? " First errors:\n" : "\n"}${preview}`,
                    { position: "top-center", autoClose: 6000 }
                );
            }
            if (ok > 0 && failures.length === 0) {
                // Full reload so /admission list and app state are fresh after import
                setTimeout(() => {
                    window.location.assign(`${window.location.origin}/admission`);
                }, 400);
            }
        } finally {
            setImporting(false);
        }
    };

    const stepMeta = [
        { n: 1, label: "Upload" },
        { n: 2, label: "Map columns" },
        { n: 3, label: "Review" },
    ];

    return (
        <div className="admission-import-page">
            <div className="admission-import-page__bg" aria-hidden />
            <div className="admission-import-page__inner">
                <header className="admission-import-header">
                    <Link
                        to="/admission"
                        className="admission-import-back"
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Back to admissions list"
                    >
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Admissions
                    </Link>
                    <div className="admission-import-header-text">
                        <h1>Import admissions</h1>
                        <p>
                            Use the same columns as <strong>Download Sample Excel</strong> on the admission list.{" "}
                            <strong>Name</strong> and <strong>Center</strong> are required. Include{" "}
                            <strong>Batch ID</strong> or <strong>Batch Name</strong> when that center has batches (same
                            rules as creating an admission manually), or set a default batch on the mapping step. Leave
                            Admission ID, Payment ID, and Created At empty for new rows — the server creates them.
                        </p>
                    </div>
                </header>

                <nav className="admission-import-stepper" aria-label="Import steps">
                    {stepMeta.map(({ n, label }) => {
                        const state =
                            currentStep === n ? "active" : currentStep > n ? "past" : "upcoming";
                        return (
                            <div
                                key={n}
                                className={`admission-import-step admission-import-step--${state}`}
                            >
                                <div className="admission-import-step__badge" aria-hidden>
                                    {state === "past" ? (
                                        <CheckCircle2 size={20} strokeWidth={2.5} />
                                    ) : (
                                        <span>{n}</span>
                                    )}
                                </div>
                                <span className="admission-import-step__label">
                                    {n}. {label}
                                </span>
                            </div>
                        );
                    })}
                </nav>

                {currentStep === 1 && (
                    <div className="admission-import-card">
                        <h2 className="admission-import-card__title">Upload spreadsheet</h2>
                        <p className="admission-import-card__subtitle">
                            CSV, XLSX, or XLS — up to 25 MB. Headers should match the sample template when possible.
                        </p>
                        <div className="admission-import-drop">
                            <div className="admission-import-drop__icon">
                                <FileSpreadsheet size={28} strokeWidth={2} aria-hidden />
                            </div>
                            <p className="admission-import-drop__cta">Drop a file here or browse</p>
                            <p className="admission-import-drop__hint">
                                We&apos;ll read the first sheet and detect column headers automatically.
                            </p>
                            <label className="admission-import-file-btn">
                                <Upload size={18} strokeWidth={2.25} aria-hidden />
                                Choose file
                                <input
                                    type="file"
                                    accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                    onChange={handleFileUpload}
                                />
                            </label>
                        </div>
                        {uploadSuccess && uploadedFileName && (
                            <div className="admission-import-success-banner" role="status">
                                <strong>{uploadedFileName}</strong> loaded — {rawExcelData.length} data row
                                {rawExcelData.length === 1 ? "" : "s"}.
                            </div>
                        )}
                        <p className="admission-import-file-meta">Supported: CSV · XLSX · XLS · max 25 MB</p>
                    </div>
                )}

                {currentStep === 2 && (
                    <div className="admission-import-card">
                        <h2 className="admission-import-card__title">Map columns</h2>
                        <p className="admission-import-card__subtitle">
                            Match each admission field to a column from your file. Required fields are marked.
                        </p>
                        {fileName && (
                            <div className="admission-import-file-pill" title={fileName}>
                                <FileSpreadsheet size={16} aria-hidden />
                                {fileName}
                            </div>
                        )}
                        <div className="admission-import-mapping-head">
                            <span>Admission field</span>
                            <span>Spreadsheet column</span>
                        </div>
                        {FIELDS.map((fieldKey) => (
                            <div
                                className={`admission-import-mapping-row${REQUIRED_KEYS.includes(fieldKey) ? " admission-import-mapping-row--required" : ""}`}
                                key={fieldKey}
                            >
                                <div className="admission-import-mapping-label">
                                    {FIELD_LABELS[fieldKey]}
                                    {REQUIRED_KEYS.includes(fieldKey) ? (
                                        <span className="req"> *</span>
                                    ) : null}
                                </div>
                                <div className="admission-import-mapping-select">
                                    <select
                                        value={fieldMappings[fieldKey] || "none"}
                                        onChange={(e) => {
                                            setFieldMappings((prev) => ({
                                                ...prev,
                                                [fieldKey]: e.target.value,
                                            }));
                                        }}
                                    >
                                        <option value="none">— Not mapped —</option>
                                        {excelHeaders.map((header, idx) => (
                                            <option key={idx} value={header}>
                                                {header}
                                                {READ_ONLY_HEADERS.has(header) ? " (read-only on export)" : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ))}

                        <div className="admission-import-default-batch">
                            <h3 className="admission-import-default-batch__title">Default batch (optional)</h3>
                            <p className="admission-import-default-batch__hint">
                                Used only when a row has no Batch ID / Batch Name. The batch must belong to the{" "}
                                <strong>same center</strong> as that row (centers with no batches stay optional).
                            </p>
                            {batchesLoad ? (
                                <p className="admission-import-default-batch__loading">Loading batches…</p>
                            ) : (
                                <select
                                    className="admission-import-default-batch__select"
                                    value={defaultBatchId}
                                    onChange={(e) => setDefaultBatchId(e.target.value)}
                                    aria-label="Default batch for import rows"
                                >
                                    <option value="">— No default (rows must specify batch if center has batches) —</option>
                                    {batches
                                        .filter((b) => isBatchActive(b))
                                        .map((b) => (
                                            <option key={b.id} value={String(b.id)}>
                                                {b.name} · {b.center_id} (ID {b.id})
                                            </option>
                                        ))}
                                </select>
                            )}
                        </div>
                    </div>
                )}

                {currentStep === 3 && (
                    <div className="admission-import-card">
                        <h2 className="admission-import-card__title">Review &amp; import</h2>
                        <p className="admission-import-card__subtitle">
                            Confirm counts below, then import. Rows missing required fields or a required batch (when that
                            center has batches) are skipped. Imported admissions default to status <strong>Active</strong>{" "}
                            if Status is empty. When you click <strong>Import admissions</strong>, we compare each row to
                            your current admissions list and to other rows in this file — if anything looks like a
                            duplicate, you&apos;ll get a warning before anything is saved.
                        </p>
                        <div className="admission-import-stats">
                            <div className="admission-import-stat admission-import-stat--ok">
                                <div className="admission-import-stat__value">{importStats.count}</div>
                                <div className="admission-import-stat__label">Ready to import</div>
                            </div>
                            <div className="admission-import-stat admission-import-stat--warn">
                                <div className="admission-import-stat__value">{importStats.skipped}</div>
                                <div className="admission-import-stat__label">Skipped (validation)</div>
                            </div>
                            <div className="admission-import-stat admission-import-stat--muted">
                                <div className="admission-import-stat__value">{importStats.unmapped}</div>
                                <div className="admission-import-stat__label">Unmapped fields</div>
                            </div>
                        </div>
                        {skippedRecords.length > 0 && (
                            <ul className="admission-import-skip-list">
                                {skippedRecords.slice(0, 20).map((r, i) => (
                                    <li key={i}>{r}</li>
                                ))}
                                {skippedRecords.length > 20 && (
                                    <li>…and {skippedRecords.length - 20} more</li>
                                )}
                            </ul>
                        )}
                    </div>
                )}

                <div className="admission-import-actions">
                    {currentStep > 1 && (
                        <button
                            type="button"
                            className="admission-import-btn admission-import-btn--secondary"
                            onClick={handleBack}
                            disabled={importing}
                        >
                            Back
                        </button>
                    )}
                    {currentStep === 3 ? (
                        <button
                            type="button"
                            className="admission-import-btn admission-import-btn--primary"
                            onClick={handleImport}
                            disabled={importing || validRows.length === 0}
                        >
                            {importing ? "Importing…" : "Import admissions"}
                        </button>
                    ) : (
                        <button type="button" className="admission-import-btn admission-import-btn--primary" onClick={handleNext}>
                            Next
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdmissionImport;
